import { useCallback, useEffect, useMemo, useState } from 'react';

import Button from 'devextreme-react/button';
import SelectBox from 'devextreme-react/select-box';
import DateBox from 'devextreme-react/date-box';
import LoadIndicator from 'devextreme-react/load-indicator';
import DataGrid, { Column, Button as Butt, Paging, Pager, Scrolling, SearchPanel, MasterDetail } from 'devextreme-react/data-grid';
import moment from 'moment';

import { useNavigate } from 'react-router-dom';

import { useApiConfigQC } from '../../../../api/api.configQC';
import { newToast } from '../../../index';
import { SysI_Analizadores } from '../../../../types/types.analizadores';
import { useScreenSize } from '../../../../utils/media-query';
import { RecepcionQcDTO, DetalleCorridaQcDTO } from '../../../../types/types.qualityControl';

import './ModalTracerByAnalyzer.scss';
import './QcReceptionsPanel.scss';

/**
 * Corridas de control de calidad que reportó un analizador.
 *
 * Vive en la pantalla de Analizadores y no en Configuración a propósito: quien opera el equipo
 * necesita ver qué está llegando y por qué algo no entra, y no tiene acceso a configuración.
 * Cargar la homologación que falta sí es tarea de configuración; verlo rebotar, no.
 *
 * El diseño es deliberadamente el mismo de la pestaña RESULTADOS (ModalTracerByAnalyzer): barra
 * azul con el total, buscador, cuatro columnas con el estado en chip y un subtexto gris, y el
 * mismo paginador. Son dos registros de lo que entra por el mismo equipo y deben leerse igual;
 * lo único que se agrega es el filtro de fechas, porque una corrida de control se busca por el
 * día en que se corrió.
 */

const ESTADOS = [
  { value: null, text: 'Todos' },
  { value: 1, text: 'Pendiente' },
  { value: 2, text: 'Procesada' },
  { value: 3, text: 'Rechazada' },
  { value: 4, text: 'Parcial' },
  { value: 5, text: 'Duplicada' },
];

/** Estado de la corrida -> variante del chip. success y error son las mismas de Resultados. */
const claseEstado = (estado: number): string => {
  switch (estado) {
    case 2:
      return 'success'; // PROCESADA
    case 4:
      return 'warning'; // PARCIAL
    case 3:
      return 'error'; // RECHAZADA
    case 5:
      return 'neutral'; // DUPLICADA
    default:
      return 'info'; // PENDIENTE
  }
};

/** Situación del código resuelto -> variante del chip, igual que en la grilla de arriba. */
const claseSituacion = (situacion: string): string => {
  switch (situacion) {
    case 'CONFIGURADO':
      return 'success';
    case 'SIN_CONFIGURACION':
      return 'warning';
    default:
      return 'neutral'; // NO_HOMOLOGADO: ruido del equipo, se ignora a propósito
  }
};

const TEXTO_SITUACION: Record<string, string> = {
  CONFIGURADO: 'Configurado',
  SIN_CONFIGURACION: 'Sin configuración',
  NO_HOMOLOGADO: 'No homologado',
};

/**
 * Detalle de una corrida: qué mandó el equipo y contra qué apunta cada código.
 *
 * Va declarado FUERA del panel a propósito. DevExtreme monta este componente al desplegar la
 * fila y lo desmonta al plegarla, así que la consulta se dispara solo ahí; si se definiera
 * dentro del panel, React lo trataría como un componente nuevo en cada render del padre y
 * volvería a pedir el detalle sin que nadie lo pida.
 *
 * El servidor resuelve con cuatro consultas fijas, no una por analito: una corrida del Cobas
 * son 25 códigos y no tiene sentido pagar 25 viajes para pintar una tabla.
 */
const DetalleCorrida = ({ fila, onNavigate }: { fila: RecepcionQcDTO; onNavigate?: () => void }) => {
  const { getQcRunDetail } = useApiConfigQC();
  const navigate = useNavigate();
  const { isXSmall } = useScreenSize();

  const [detalle, setDetalle] = useState<DetalleCorridaQcDTO[]>([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let vigente = true;

    getQcRunDetail(fila.Qcra_Id).then((res) => {
      if (!vigente) return;
      setDetalle(Array.isArray(res) ? res : []);
      setCargando(false);
    });

    // Si se pliega la fila antes de que responda, no se toca el estado de un componente muerto.
    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fila.Qcra_Id]);

  const fecha = (valor: any) => {
    const m = moment(valor);
    return m.isValid() ? m.format('DD/MM/YYYY hh:mm A') : '—';
  };

  /**
   * Clic en la fila -> el control de ese analito, en el registro masivo.
   *
   * Va al masivo y no a /measurements-qc porque el masivo es el que usa el laboratorio.
   * Se manda la configuración por la URL y allá se selecciona el material/lote y se abre el
   * analito, que es lo que haría el usuario a mano.
   */
  const abrirControl = useCallback(
    (e: any) => {
      const item: DetalleCorridaQcDTO = e.data;
      if (!item) return;

      if (!item.Qcc_Id) {
        // Sin configuración no hay control que abrir. Se dice por qué en vez de no hacer nada.
        const motivo =
          item.Situacion === 'NO_HOMOLOGADO'
            ? `"${item.Test}" no corresponde a ningún analito de este equipo.`
            : `"${item.AnalitoNombre || item.Test}" no tiene configuración QC en este lote.`;
        newToast('WARNING', motivo, 5);
        return;
      }

      // Cuando el panel vive dentro del modal del inicio hay que cerrarlo: el modal lo
      // gobierna un singleton, y si se navega con el abierto vuelve a aparecer al regresar.
      onNavigate?.();
      navigate(`/masive-measurements-qc?config=${item.Qcc_Id}`);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, onNavigate]
  );

  // Solo se ve clicable lo que lleva a algún lado.
  const prepararFila = useCallback((e: any) => {
    if (e.rowType === 'data' && e.data?.Qcc_Id) {
      e.rowElement.classList.add('qc-fila-clicable');
    }
  }, []);

  const renderSituacion = useCallback((cell: any) => {
    const situacion: string = cell.value;
    return <div className={`status-chip ${claseSituacion(situacion)}`}>{TEXTO_SITUACION[situacion] || situacion}</div>;
  }, []);

  const renderAnalito = useCallback((cell: any) => {
    const item: DetalleCorridaQcDTO = cell.data;
    if (!item.AnalitoNombre) return <span className='qc-sin-analito'>—</span>;

    return (
      <div className='analite-cell'>
        <div className='analite-main'>{item.AnalitoNombre}</div>
        {item.Situacion === 'SIN_CONFIGURACION' && <div className='analite-sub'>Falta configuración QC en este lote</div>}
      </div>
    );
  }, []);

  return (
    <div className='qc-detail'>
      <div className='qc-detail-facts'>
        <div>
          <span>Fecha del equipo</span>
          <span>{fecha(fila.Qcra_FechaAnalizador)}</span>
        </div>
        <div>
          <span>Recibida</span>
          <span>{fecha(fila.Qcra_FechaRecepcion)}</span>
        </div>
        <div>
          <span>Recibidos</span>
          <span>{fila.Qcra_TotalRecibidos ?? 0}</span>
        </div>
        <div>
          <span>Registrados</span>
          <span>{fila.Qcra_TotalRegistrados ?? 0}</span>
        </div>
        <div>
          <span>Sin configuración</span>
          <span>{fila.Qcra_TotalSinConfiguracion ?? 0}</span>
        </div>
      </div>

      {fila.Qcra_Motivo && (
        <div className='qc-detail-reason'>
          <div className='qc-reason-label'>Motivo</div>
          <div className='qc-reason-text'>{fila.Qcra_Motivo}</div>
        </div>
      )}

      {cargando ? (
        <div className='qc-detail-empty'>
          <LoadIndicator visible={true} height={18} width={18} /> Resolviendo la corrida...
        </div>
      ) : detalle.length === 0 ? (
        <div className='qc-detail-empty'>La corrida no trae detalle.</div>
      ) : (
        <DataGrid dataSource={detalle} showBorders={false} showRowLines={true} showColumnLines={false} rowAlternationEnabled={true} hoverStateEnabled={true} columnAutoWidth={false} wordWrapEnabled={true} height={260} onRowClick={abrirControl} onRowPrepared={prepararFila}>
          <Scrolling mode='virtual' />
          {/*
            Anchos al tamaño del dato y ANALITO como única columna elástica: los códigos del
            equipo son de cinco caracteres y el valor de uno o dos, así que reservarles 170 px
            empujaba ESTADO fuera del panel y obligaba a hacer scroll horizontal para ver lo
            que más importa. La unidad se retira en pantallas de teléfono porque es el único
            dato que ya está en la configuración del control.
          */}
          <Column caption='Código' dataField='Test' alignment='left' width={110} />
          <Column caption='Valor' dataField='Result' alignment='left' width={85} />
          <Column caption='Unidad' dataField='Unidad' alignment='left' width={85} visible={!isXSmall} />
          <Column caption='Analito' dataField='AnalitoNombre' alignment='left' minWidth={160} cellRender={renderAnalito} />
          <Column caption='Estado' dataField='Situacion' alignment='left' width={160} cellRender={renderSituacion} />
        </DataGrid>
      )}
    </div>
  );
};

const QcReceptionsPanel = ({ analyzer, onNavigate }: { analyzer: SysI_Analizadores; onNavigate?: () => void }) => {
  const { getQcReceptions, reprocessQcReception } = useApiConfigQC();

  const [recepciones, setRecepciones] = useState<RecepcionQcDTO[]>([]);
  const [cargando, setCargando] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState<number | null>(null);
  const [filtroDesde, setFiltroDesde] = useState<Date | undefined>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d;
  });

  const consultar = useCallback(async () => {
    if (!analyzer?.Az_Id) return;
    setCargando(true);
    const res = await getQcReceptions({
      AnalizadorId: analyzer.Az_Id,
      FechaDesde: filtroDesde ? filtroDesde.toISOString() : null,
      Estado: filtroEstado,
    });
    setRecepciones(Array.isArray(res) ? res : []);
    setCargando(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyzer?.Az_Id, filtroDesde, filtroEstado]);

  useEffect(() => {
    consultar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyzer?.Az_Id]);

  const handleReprocesar = useCallback(
    async (e) => {
      const fila: RecepcionQcDTO = e.row.data;
      const ok = await reprocessQcReception(fila.Qcra_Id);
      if (ok) {
        newToast('SUCCESS', 'Corrida reprocesada', 3);
        consultar();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [consultar]
  );

  // Repetir una corrida ya PROCESADA duplicaría sus mediciones; el backend la rechaza, así que
  // no se ofrece el botón.
  const puedeReprocesar = useCallback((e) => e.row?.data?.Qcra_Estado !== 2, []);

  const total = useMemo(() => recepciones.length, [recepciones]);

  // ── Celdas, con la misma forma que las de Resultados ──────────────────────

  const renderEstado = useCallback(
    (cell) => <div className={`status-chip ${claseEstado(cell.data.Qcra_Estado)}`}>{cell.data.EstadoNombre}</div>,
    []
  );

  /**
   * Se muestra la fecha del EQUIPO, no la de recepción: es la que ubica el punto en la carta
   * de Levey-Jennings y la que define el turno. La de recepción va en el detalle.
   */
  const renderFecha = useCallback((cell) => {
    const valor = cell.data.Qcra_FechaAnalizador || cell.data.Qcra_FechaRecepcion;
    const m = moment(valor);
    if (!m.isValid()) return <div className='date-cell'><div className='date-main'>—</div></div>;

    return (
      <div className='date-cell'>
        <div className='date-main'>{m.format('DD/MM/YYYY')}</div>
        <div className='time-sub'>{m.format('hh:mm A')}</div>
      </div>
    );
  }, []);

  const renderControl = useCallback(
    (cell) => (
      <div className='order-cell'>
        <div className='order-main'>{cell.value || 'N/A'}</div>
      </div>
    ),
    []
  );

  /**
   * Mismo patrón que la columna ANALITO de Resultados: arriba el dato y debajo, en gris, qué
   * pasó con él. Ahí va el motivo cuando lo hay, porque es lo único que explica un rechazo; y
   * cuando no lo hay, el conteo de lo registrado.
   */
  const renderCorrida = useCallback((cell) => {
    const fila: RecepcionQcDTO = cell.data;
    const principal = fila.LoteCodigo || 'Sin homologar';

    const registradas = `${fila.Qcra_TotalRegistrados ?? 0} de ${fila.Qcra_TotalRecibidos ?? 0} mediciones registradas`;
    const sinConfig = fila.Qcra_TotalSinConfiguracion ? ` · ${fila.Qcra_TotalSinConfiguracion} sin configuración` : '';
    const detalle = fila.Qcra_Motivo || registradas + sinConfig;

    return (
      <div className='analite-cell' title={detalle}>
        <div className='analite-main'>{principal}</div>
        <div className='analite-sub'>{detalle}</div>
      </div>
    );
  }, []);

  const renderDetalle = useCallback((props) => <DetalleCorrida fila={props.data} onNavigate={onNavigate} />, [onNavigate]);

  // ── Contenido ─────────────────────────────────────────────────────────────

  if (cargando) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <LoadIndicator visible={true} />
        <div style={{ marginTop: '1rem' }}>Cargando corridas de control...</div>
      </div>
    );
  }

  return (
    <div className='analyzer-results qc-receptions'>
      <div className='qc-filters'>
        <div className='qc-field'>
          <label>Desde</label>
          <DateBox value={filtroDesde} onValueChanged={(e) => setFiltroDesde(e.value)} type='date' displayFormat='dd/MM/yyyy' height={32} stylingMode='outlined' />
        </div>
        <div className='qc-field'>
          <label>Estado</label>
          <SelectBox dataSource={ESTADOS} valueExpr='value' displayExpr='text' value={filtroEstado} onValueChanged={(e) => setFiltroEstado(e.value)} height={32} stylingMode='outlined' />
        </div>
        <Button icon='search' text='Consultar' stylingMode='contained' type='default' height={32} disabled={cargando} onClick={consultar} />
        <div className='qc-filters-spacer' />
      </div>

      <div className='analyzer-results-summary'>
        <span className='summary-count'>Total: {total > 0 ? total.toLocaleString() : '0'}</span>
      </div>

      <DataGrid
        dataSource={recepciones}
        keyExpr='Qcra_Id'
        showBorders={false}
        showRowLines={true}
        showColumnLines={false}
        rowAlternationEnabled={true}
        hoverStateEnabled={true}
        height='100%'
        columnAutoWidth={false}
        wordWrapEnabled={true}
        noDataText='Este analizador no ha reportado controles en el rango seleccionado'
      >
        <SearchPanel visible={true} placeholder='Buscar corridas...' />
        <Scrolling mode='standard' />
        <Paging enabled={true} defaultPageSize={50} />
        <Pager visible={true} allowedPageSizes={[25, 50, 100, 200, 'all']} displayMode='full' showPageSizeSelector={true} showInfo={true} showNavigationButtons={true} />
        <MasterDetail enabled={true} render={renderDetalle} />

        {/*
          CORRIDA es la única que crece: lleva el lote y, cuando algo falla, el motivo. Las
          demás tienen un ancho fijo del tamaño de su dato para que el panel entre igual en la
          pantalla de Analizadores que en el modal del inicio, que es bastante más angosto.
        */}
        <Column dataField='Qcra_Estado' caption='Estado' width={115} alignment='left' cellRender={renderEstado} allowSorting={true} allowFiltering={false} />
        <Column dataField='Qcra_FechaAnalizador' caption='Fecha' width={115} cellRender={renderFecha} allowSorting={true} sortOrder='desc' allowFiltering={false} />
        <Column dataField='Qcra_LoteReportado' caption='Control' width={110} cellRender={renderControl} allowSorting={true} />
        <Column dataField='LoteCodigo' caption='Corrida' minWidth={180} cellRender={renderCorrida} allowSorting={true} />
        <Column type='buttons' width={55}>
          <Butt hint='Reprocesar' icon='refresh' onClick={handleReprocesar} visible={puedeReprocesar} />
        </Column>
      </DataGrid>
    </div>
  );
};

export default QcReceptionsPanel;
